import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Layers, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  EmptyState,
  EntityDrawer,
  PageHeader,
  StatutoryDefaultsCallout,
} from "../components/ui";
import { BilingualText } from "../components/ui/BilingualText";
import { api } from "../lib/api";
import type { GradeDetail } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// Tree-view list of Grades for a LegalEntity (M13.4).
// LE id from ?le=<uuid>, same convention as the other M10-M13 pages.

interface TreeNode {
  grade: GradeDetail;
  children: TreeNode[];
}

function buildTree(grades: GradeDetail[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  grades.forEach((g) => byId.set(g.id, { grade: g, children: [] }));
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.grade.parent_grade_id && byId.has(node.grade.parent_grade_id)) {
      byId.get(node.grade.parent_grade_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function GradeNode({ node, depth }: { node: TreeNode; depth: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;
  return (
    <div className="flex flex-col">
      <div
        className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2"
        style={{ marginInlineStart: depth * 24 }}
      >
        <button
          type="button"
          onClick={() => setOpen(!open)}
          disabled={!hasChildren}
          className={`inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 ${
            hasChildren ? "hover:bg-slate-100 hover:text-slate-700" : "opacity-30"
          }`}
        >
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <Link
          to={`/grades/${node.grade.id}`}
          className="flex-1 text-sm font-medium text-slate-900 hover:underline"
        >
          <span className="me-1.5 font-mono text-xs text-slate-500">
            {node.grade.code}
          </span>
          <BilingualText
            en={node.grade.name_en}
            ar={node.grade.name_ar}
            layout="inline"
            secondaryClassName="ms-2 text-xs text-slate-400"
          />
        </Link>
        {node.grade.steps.length > 0 && (
          <span className="text-xs text-slate-500">
            {t("policies.grades_steps_count", { count: node.grade.steps.length })}
          </span>
        )}
      </div>
      {hasChildren && open && (
        <div className="mt-1 flex flex-col gap-1">
          {node.children.map((c) => (
            <GradeNode key={c.grade.id} node={c} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function NewGradeDialog({
  legalEntityId,
  parentOptions,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  parentOptions: GradeDetail[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    code: "",
    name_en: "",
    name_ar: "",
    parent_grade_id: "",
    description: "",
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.createGrade({
        legal_entity_id: legalEntityId,
        code: form.code,
        name_en: form.name_en,
        name_ar: form.name_ar,
        parent_grade_id: form.parent_grade_id || null,
        description: form.description || null,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policies.grades_dialog_title")}
      description={t("policies.grades_dialog_desc")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="new-grade-form"
            disabled={mutation.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
          >
            {mutation.isPending
              ? t("policies.creating")
              : t("policies.create")}
          </button>
        </div>
      }
    >
        <form
          id="new-grade-form"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="space-y-3 text-sm"
        >
          <label className="block">
            <span className="font-medium text-slate-700">{t("policies.code_required")}</span>
            <input
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-medium text-slate-700">{t("policies.name_en_required")}</span>
              <input
                required
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="font-medium text-slate-700">{t("policies.name_ar_required")}</span>
              <input
                required
                dir="rtl"
                value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>
          </div>
          <label className="block">
            <span className="font-medium text-slate-700">{t("policies.grades_parent_grade")}</span>
            <select
              value={form.parent_grade_id}
              onChange={(e) =>
                setForm({ ...form, parent_grade_id: e.target.value })
              }
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            >
              <option value="">{t("policies.grades_no_parent")}</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name_en}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="font-medium text-slate-700">{t("policies.description_label")}</span>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            />
          </label>
          {mutation.error && (
            <p className="rounded bg-rose-50 p-2 text-rose-700">
              {(mutation.error as Error).message}
            </p>
          )}
        </form>
    </EntityDrawer>
  );
}

export function GradesPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["grades", le],
    queryFn: () => api.listGrades(le),
    enabled: !!le,
  });

  if (gate) return gate;
  if (isLoading) return <Loading />;
  if (error) return <ErrorPanel message={(error as Error).message} />;

  const grades = data ?? [];
  const tree = buildTree(grades);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t("policies.grades_title")}
        subtitle={t("policies.grades_count", { count: grades.length })}
        actions={
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> {t("policies.grades_new")}
          </button>
        }
      />

      <StatutoryDefaultsCallout domain="grade" />

      {grades.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border-default bg-surface-default">
          <EmptyState
            icon={Layers}
            headline={t("policies.grades_empty")}
            description={t(
              "policies.grades_empty_desc",
              "Grades are the master entitlement bundles employees inherit through their position. Add your first grade to start shaping payroll, leave, and EOS entitlements.",
            )}
            action={{
              label: t("policies.grades_new"),
              onClick: () => setShowNew(true),
            }}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {tree.map((n) => (
            <GradeNode key={n.grade.id} node={n} depth={0} />
          ))}
        </div>
      )}

      {showNew && (
        <NewGradeDialog
          legalEntityId={le}
          parentOptions={grades}
          onClose={() => setShowNew(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ["grades"] })}
        />
      )}
    </div>
  );
}
